"use client";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface AccountTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContinue: (accountType: "homeowner" | "contractor") => void;
  isLoginFlow?: boolean; // New prop to distinguish login vs signup
}

export default function AccountTypeModal({
  open,
  onOpenChange,
  onContinue,
  isLoginFlow = false, // Default to signup flow
}: AccountTypeDialogProps) {
  const [selectedType, setSelectedType] = useState<"homeowner" | "contractor">(
    "homeowner"
  );

  const handleConfirm = () => {
    onContinue(selectedType);
  };

  const handleCancel = () => {
    if (!isLoginFlow) {
      // Only allow cancel for signup flow
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[440px]"
        data-testid="account-type-modal"
      >
        <DialogHeader className="text-center space-y-3">
          <DialogTitle
            className="lg:text-[26px] text-[20px] font-semibold text-gray-900 leading-tight"
            data-testid="account-type-modal-title"
          >
            Confirm Your Account Type
          </DialogTitle>
          <p
            className="text-gray-600 text-base"
            data-testid="account-type-modal-description"
          >
            {isLoginFlow ? (
              "Please select the type of account you would like to create."
            ) : (
              <>
                You&apos;re about to sign up with your <strong>google</strong>{" "}
                account. Please confirm your account type to continue.
              </>
            )}
          </p>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {/* Account Type Selection */}
          <div className="space-y-3">
            <Tabs
              value={selectedType}
              onValueChange={(value) =>
                setSelectedType(value as "homeowner" | "contractor")
              }
              className="w-full"
              data-testid="account-type-modal-tabs"
            >
              <TabsList className="bg-gray-100 p-0.5 px-2 h-10 rounded-lg w-full">
                <TabsTrigger
                  value="homeowner"
                  className="flex-1 p-1.5 cursor-pointer text-sm rounded-md data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm data-[state=inactive]:text-gray-600 transition-all"
                  data-testid="account-type-modal-homeowner"
                >
                  I&apos;m a homeowner
                </TabsTrigger>
                <TabsTrigger
                  value="contractor"
                  className="flex-1 p-1.5 cursor-pointer text-sm rounded-md data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm data-[state=inactive]:text-gray-600 transition-all"
                  data-testid="account-type-modal-contractor"
                >
                  I&apos;m a contractor
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Action Buttons */}
          <div
            className={
              isLoginFlow ? "flex justify-center" : "grid grid-cols-2 gap-3"
            }
          >
            {!isLoginFlow && (
              <Button
                size="lg"
                type="button"
                variant="outline"
                onClick={handleCancel}
                data-testid="account-type-modal-cancel"
              >
                Cancel
              </Button>
            )}
            <Button
              size="lg"
              type="button"
              onClick={handleConfirm}
              className={isLoginFlow ? "w-full" : ""}
              data-testid="account-type-modal-continue"
            >
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
